import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import supabase from '../supabaseClient';
import './DashboardPage.css';

const DashboardPage = () => {
  const { member } = useAuth();
  const [latestEditions, setLatestEditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referralEmail, setReferralEmail] = useState('');
  const [referralLinkedIn, setReferralLinkedIn] = useState('');
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralSuccess, setReferralSuccess] = useState('');

  useEffect(() => {
    const fetchLatestEditions = async () => {
      try {
        const { data, error } = await supabase
          .from('editions')
          .select('*')
          .lt('event_date', new Date().toISOString())
          .order('event_date', { ascending: false })
          .limit(4);

        if (error) throw error;
        setLatestEditions(data || []);
      } catch (error) {
        console.error('Error fetching editions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestEditions();
  }, []);

  const handleSponsorSubmit = async (e) => {
    e.preventDefault();
    setReferralLoading(true);
    setReferralSuccess('');

    try {
      const response = await fetch('/.netlify/functions/notify-referral', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          referrer_id: member?.id,
          referred_email: referralEmail,
          referred_linkedin: referralLinkedIn,
        }),
      });

      if (response.ok) {
        setReferralSuccess('Parrainage envoyé avec succès !');
        setReferralEmail('');
        setReferralLinkedIn('');
        setTimeout(() => setReferralSuccess(''), 5000);
      } else {
        const error = await response.json();
        alert(error.message || 'Erreur lors de l\'envoi du parrainage');
      }
    } catch (error) {
      console.error('Referral error:', error);
      alert('Erreur lors de l\'envoi du parrainage');
    } finally {
      setReferralLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <div className="member-badge">
            <span className="badge-dot"></span>
            Espace Membres
          </div>
          <h1>Bienvenue, {member?.name}</h1>
          <p className="dashboard-subtitle">Votre espace privé B2B</p>
        </div>

        {/* Quick Links Grid */}
        <div className="dashboard-grid">
          <Link to="/members/editions" className="dashboard-card editions-card">
            <div className="card-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="8" y="8" width="24" height="24" rx="2" fill="currentColor" />
              </svg>
            </div>
            <h3>Éditions</h3>
            <p>Accédez aux éditions passées et à venir</p>
          </Link>

          <Link to="/members/directory" className="dashboard-card directory-card">
            <div className="card-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="13" cy="12" r="4" fill="currentColor" />
                <path d="M7 20C7 17.24 9.69 15 13 15C16.31 15 19 17.24 19 20V23H7V20Z" fill="currentColor" />
                <circle cx="27" cy="12" r="4" fill="currentColor" />
                <path d="M21 20C21 17.24 23.69 15 27 15C30.31 15 33 17.24 33 20V23H21V20Z" fill="currentColor" />
              </svg>
            </div>
            <h3>Annuaire</h3>
            <p>Retrouvez les autres membres</p>
          </Link>

          <form onSubmit={handleSponsorSubmit} className="dashboard-card sponsor-card">
            <div className="card-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 2L25 15H38L28 22L33 35L20 28L7 35L12 22L2 15H15L20 2Z" fill="currentColor" />
              </svg>
            </div>
            <h3>Parrainer un membre</h3>
            <p>Recommandez quelqu'un pour The Recap</p>
            <div className="sponsor-form">
              <input
                type="email"
                placeholder="Email"
                value={referralEmail}
                onChange={(e) => setReferralEmail(e.target.value)}
                required
              />
              <input
                type="text"
                placeholder="URL LinkedIn (optionnel)"
                value={referralLinkedIn}
                onChange={(e) => setReferralLinkedIn(e.target.value)}
              />
              <button type="submit" disabled={referralLoading}>
                {referralLoading ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
            {referralSuccess && <div className="success-message">{referralSuccess}</div>}
          </form>
        </div>

        {/* Latest Editions Section */}
        <div className="latest-editions-section">
          <h2>Dernières éditions</h2>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div className="spinner"></div>
            </div>
          ) : latestEditions.length > 0 ? (
            <div className="editions-cards-grid">
              {latestEditions.map((edition) => (
                <Link
                  key={edition.id}
                  to={`/members/editions/${edition.id}`}
                  className="edition-card"
                >
                  {edition.cover_image && (
                    <div className="edition-cover">
                      <img src={edition.cover_image} alt={edition.title} />
                    </div>
                  )}
                  <div className="edition-content">
                    <div className="edition-date">{formatDate(edition.event_date)}</div>
                    <h4>{edition.title}</h4>
                    {edition.description && (
                      <p className="edition-description">{edition.description}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="no-editions">Aucune édition disponible pour le moment</p>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        .badge-dot {
          display: inline-block;
          width: 8px;
          height: 8px;
          background-color: #4caf50;
          border-radius: 50%;
          margin-right: 8px;
          animation: pulse 2s ease-in-out infinite;
        }
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #1f5d5b;
          border-top: 4px solid transparent;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DashboardPage;
